import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { faPenToSquare, faSparkles } from '@awesome.me/kit-4a6bf60416/icons/classic/regular';
import { faSpinnerScale } from '@awesome.me/kit-4a6bf60416/icons/classic/solid';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import axios from 'axios';
import { toast } from 'sonner';

import { calculateFileChecksum } from '@/components/asset-manager/utils';
import { AddPageContent } from '@/components/page-editor/add-page-modal/add-page-content';
import {
  BLANK_PAGE_OPTION,
  CASE_RESULT_LAYOUT_HEADER,
} from '@/components/page-editor/add-page-modal/page-template-types';
import { DocumentUploadStep } from '@/components/page-editor/document-upload-step';
import { AI_TOAST_MESSAGES } from '@/components/shared/ai-menu-constants';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { FeaturedIcon, Icon } from '@/components/ui/icon';
import { Separator } from '@/components/ui/separator';
import { MutedText, Paragraph } from '@/components/ui/typography';
import { useSectionTemplates } from '@/hooks/use-section-templates';
import {
  fetchSpecialPages,
  useSpecialPagesBeforeOpen,
} from '@/hooks/use-special-pages-before-open';
import { showAiCreditsExhaustedModal, useAiCreditsExhausted } from '@/lib/ai-credits-gate';
import { getCable } from '@/lib/cable';
import http, { extractApiErrorMessage } from '@/lib/http';
import { buildSectionsData } from '@/lib/page-utils';
import {
  cmsPagesEditorIndexPath,
  cmsPageTemplatesPath,
  directUploadCmsCaseResultsPath,
  extractCmsCaseResultsPath,
} from '@/lib/routes';
import { cn, formatErrorMessage, randomId } from '@/lib/utils';
import { useAiOperationsStore } from '@/stores/ai-operations-store';
import { usePagesActions } from '@/stores/pages-store';
import type { SectionStructure } from '@/types/page';
import { PageType } from '@/types/page';
import type { SpecialPageMeta } from '@/types/shared';

const DEFAULT_PAGE_METADATA = {
  slots: {
    header: { visible: true, variant: 'default' },
    footer: { visible: true, variant: 'default' },
  },
};

const LOADING_STEPS = ['Reading document', 'Identifying case details', 'Creating case result'];

function CaseResultLoadingState({ step }: { step: 1 | 2 | 3 }) {
  const [dotCount, setDotCount] = useState(1);

  useEffect(() => {
    const interval = setInterval(() => setDotCount((prev) => (prev % 3) + 1), 400);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex flex-col items-center gap-4 px-10 py-4">
      <output aria-label="Loading">
        <Icon
          icon={faSpinnerScale}
          className="text-primary text-[96px]"
          style={{ animation: 'spin 1.5s linear infinite' }}
        />
      </output>
      <ul className="space-y-1 self-start text-base">
        {LOADING_STEPS.map((label, i) => {
          const active = i === step - 1;
          return (
            <li key={label} className={active ? 'text-foreground' : 'text-muted-foreground'}>
              {label}
              {active && <span className="inline-block w-8">{'.'.repeat(dotCount)}</span>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

interface Choice {
  key: string;
  icon: IconDefinition;
  title: string;
  description: string;
  onClick: () => void;
  disabled?: boolean;
}

function ChoiceGrid({ className, choices }: { className?: string; choices: Choice[] }) {
  return (
    <div className={cn('grid grid-cols-1 gap-4 sm:grid-cols-2', className)}>
      {choices.map(({ key, icon, title, description, onClick, disabled }) => (
        <button
          key={key}
          type="button"
          onClick={onClick}
          disabled={disabled}
          className={cn(
            'group flex flex-col items-center gap-4 rounded-lg border border-border bg-background p-8 text-center transition-colors',
            'hover:border-primary/40 hover:bg-primary/5',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            'disabled:cursor-not-allowed disabled:opacity-50',
          )}
        >
          <FeaturedIcon icon={icon} size="lg" colorScheme="primary" variant="filled" />
          <div className="space-y-1">
            <Paragraph className="font-semibold">{title}</Paragraph>
            <MutedText className="text-sm leading-tight text-balance">{description}</MutedText>
          </div>
        </button>
      ))}
    </div>
  );
}

type Step = 'method' | 'templates' | 'upload';

interface AddCaseResultModalProps {
  trigger?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onCloseAutoFocus?: (event: Event) => void;
}

interface ExtractedCaseResult extends Record<string, unknown> {
  title?: string;
  summary?: string;
  body?: string;
}

export function AddCaseResultModal({
  trigger,
  open,
  onOpenChange,
  onCloseAutoFocus,
}: AddCaseResultModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState<Step>('method');
  const [loadingStep, setLoadingStep] = useState<1 | 2 | 3 | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [pendingTemplateCode, setPendingTemplateCode] = useState<string | undefined>();
  const [pendingSections, setPendingSections] = useState<SectionStructure[] | undefined>();

  const { createPage } = usePagesActions();
  const isAiStreaming = useAiOperationsStore((state) => state.isStreaming);
  const creditsExhausted = useAiCreditsExhausted();
  const { templatesByCode, isLoading: isLoadingSectionTemplates } = useSectionTemplates();
  const { specialPages, prepareOpen, close } = useSpecialPagesBeforeOpen();
  const hasCaseResults = specialPages?.hasCaseResults ?? false;
  const storedLayoutCode = specialPages?.caseResultLayoutCode ?? undefined;

  // Resolve the stored layout before opening so the dialog renders once on the
  // correct step instead of flashing the template picker while special pages load.
  const openWithResolvedStep = useCallback((resolved: SpecialPageMeta | undefined) => {
    setStep(resolved?.hasCaseResults && resolved?.caseResultLayoutCode ? 'method' : 'templates');
    setIsOpen(true);
  }, []);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        close();
        setIsOpen(false);
        onOpenChange?.(false);
        return;
      }
      void prepareOpen(openWithResolvedStep);
    },
    [close, onOpenChange, prepareOpen, openWithResolvedStep],
  );

  useEffect(() => {
    if (open === undefined) return;
    if (open) {
      void prepareOpen(openWithResolvedStep);
    } else {
      close();
      setIsOpen(false);
    }
    return close;
  }, [open, prepareOpen, openWithResolvedStep, close]);

  useEffect(() => {
    if (!isOpen) {
      setLoadingStep(null);
      setUploadError(null);
      setPendingTemplateCode(undefined);
      setPendingSections(undefined);
      return;
    }
    // Prefill the locked layout only when case results exist; otherwise the fresh pick must win.
    if (step !== 'method' || !storedLayoutCode || !hasCaseResults) return;
    let cancelled = false;
    void (async () => {
      try {
        const templatesResponse = await http.get<{
          data: Array<{ code: string; sectionsStructure: SectionStructure[] }>;
        }>(cmsPageTemplatesPath(), {
          params: { pageType: PageType.CASE_RESULT },
        });
        if (cancelled) return;

        const matched = templatesResponse.data.data?.find((t) => t.code === storedLayoutCode);
        if (!matched) {
          setStep('templates');
          return;
        }
        setPendingTemplateCode(storedLayoutCode);
        setPendingSections(matched.sectionsStructure);
      } catch {
        if (!cancelled) setStep('templates');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, step, storedLayoutCode, hasCaseResults]);

  const ensureIndexExists = useCallback(async () => {
    const specialPages = await fetchSpecialPages().catch(() => undefined);
    if (specialPages?.hasCaseResultIndex) return;

    await http.post(cmsPagesEditorIndexPath(), {
      pageType: PageType.CASE_RESULT_INDEX,
      metadata: DEFAULT_PAGE_METADATA,
    });
  }, []);

  const createCaseResult = useCallback(
    async (extracted?: ExtractedCaseResult) => {
      const templateCode = pendingTemplateCode;
      const sectionsStructure = pendingSections;
      if (isAiStreaming) {
        toast.error(AI_TOAST_MESSAGES.OPERATION_IN_PROGRESS);
        setLoadingStep(null);
        return;
      }
      if (isLoadingSectionTemplates) {
        toast.error('Section templates are still loading. Please try again in a moment.');
        setLoadingStep(null);
        return;
      }

      setLoadingStep(3);

      try {
        await ensureIndexExists();

        const sectionsData = sectionsStructure
          ? await buildSectionsData(sectionsStructure, templatesByCode)
          : undefined;

        await createPage(
          {
            pageType: PageType.CASE_RESULT,
            ...(templateCode && { pageTemplateCode: templateCode }),
            ...(sectionsData && { sections: sectionsData }),
            ...(extracted && { contentModel: extracted }),
            metadata: DEFAULT_PAGE_METADATA,
          },
          {
            onSuccess: () => handleOpenChange(false),
            onError: (errors) => {
              toast.error(formatErrorMessage(errors, 'Failed to create case result'));
              setLoadingStep(null);
            },
          },
        );
      } catch (error) {
        toast.error(extractApiErrorMessage(error) ?? 'Failed to create case result');
        setLoadingStep(null);
      }
    },
    [
      ensureIndexExists,
      handleOpenChange,
      createPage,
      templatesByCode,
      isAiStreaming,
      isLoadingSectionTemplates,
      pendingTemplateCode,
      pendingSections,
    ],
  );

  const handleUpload = useCallback(
    async (uploadedFiles: File[]) => {
      if (creditsExhausted) {
        showAiCreditsExhaustedModal();
        return;
      }

      setLoadingStep(1);
      setUploadError(null);

      const runId = randomId();
      const failUpload = (message?: string) => {
        setUploadError(message ?? 'The document(s) could not be processed. Please try again.');
        setLoadingStep(null);
      };

      const subscription = getCable().subscriptions.create(
        { channel: 'CaseResultExtractionChannel', runId },
        {
          received: (event: {
            step?: string;
            status?: string;
            message?: string;
            code?: string;
            data?: ExtractedCaseResult;
          }) => {
            if (event?.step !== 'extracting') return;
            if (event.status === 'started') {
              setLoadingStep(2);
            } else if (event.status === 'completed') {
              subscription.unsubscribe();
              void createCaseResult(event.data);
            } else if (event.status === 'failed') {
              subscription.unsubscribe();
              if (event.code === 'ai_credits_exhausted') {
                handleOpenChange(false);
                showAiCreditsExhaustedModal(event.message);
              } else {
                failUpload(event.message);
              }
            }
          },
        },
      );

      try {
        // Upload every file straight to S3 in parallel so no bytes pass through
        // Rails, then hand the extract endpoint only the blob signed-ids.
        const blobSignedIds = await Promise.all(
          uploadedFiles.map(async (file) => {
            const checksum = await calculateFileChecksum(file);
            const prepare = await http.post(directUploadCmsCaseResultsPath(), {
              filename: file.name,
              contentType: file.type,
              byteSize: file.size,
              checksum,
            });
            const { directUpload, blobSignedId } = prepare.data;

            await axios.put(directUpload.url, file, {
              headers: { ...directUpload.headers, 'Content-Type': file.type },
            });
            return blobSignedId as string;
          }),
        );

        // Returns 202 immediately; the extracted result arrives over the cable.
        await http.post(extractCmsCaseResultsPath(), {
          runId,
          blobSignedIds,
        });
      } catch (error) {
        subscription.unsubscribe();
        const backendError = (error as { response?: { data?: { error?: string } } })?.response?.data
          ?.error;
        failUpload(backendError);
      }
    },
    [createCaseResult, creditsExhausted, handleOpenChange],
  );

  const dialogSize = step === 'templates' ? '4xl' : 'xl';

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent size={dialogSize} onCloseAutoFocus={onCloseAutoFocus}>
        {step === 'upload' && loadingStep !== null ? (
          <DialogHeader className="sr-only">
            <DialogTitle>Generating Case Result</DialogTitle>
            <DialogDescription>
              Reading the uploaded document(s) and drafting your Case Result.
            </DialogDescription>
          </DialogHeader>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>
                {step === 'upload'
                  ? 'Upload Your Case Result'
                  : step === 'templates'
                    ? CASE_RESULT_LAYOUT_HEADER.title
                    : 'Create Your Case Result'}
              </DialogTitle>
              <DialogDescription>
                {step === 'method' &&
                  'You can write your own or upload a document and let AI generate a summary.'}
                {step === 'templates' && CASE_RESULT_LAYOUT_HEADER.description}
                {step === 'upload' &&
                  'Upload a settlement agreement, verdict, or related document and let AI help draft your Case Result. You can upload multiple documents related to the same case.'}
              </DialogDescription>
            </DialogHeader>
            {step === 'templates' && <Separator className="mt-2" />}
          </>
        )}

        {step === 'method' && (
          <ChoiceGrid
            className="pt-2"
            choices={[
              {
                key: 'blank',
                icon: faPenToSquare,
                title: 'Write My Own',
                description: 'Write your own case result manually',
                disabled: loadingStep !== null,
                onClick: () => createCaseResult(),
              },
              {
                key: 'ai',
                icon: faSparkles,
                title: 'Create With AI',
                description: 'Upload a document and let AI generate a summary',
                onClick: () => setStep('upload'),
              },
            ]}
          />
        )}

        {step === 'templates' && (
          <AddPageContent
            selectedItem={BLANK_PAGE_OPTION.key}
            templatesByCode={templatesByCode}
            isLoadingSectionTemplates={isLoadingSectionTemplates}
            onAddTemplate={(_pageType, templateCode, sectionsStructure) => {
              setPendingTemplateCode(templateCode);
              setPendingSections(sectionsStructure);
              setStep('method');
            }}
            isCreating={loadingStep !== null}
            childPageType={PageType.CASE_RESULT}
          />
        )}

        {step === 'upload' &&
          (loadingStep !== null ? (
            <CaseResultLoadingState step={loadingStep} />
          ) : (
            <DocumentUploadStep
              accept={{
                'application/pdf': ['.pdf'],
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
                  '.docx',
                ],
                'application/msword': ['.doc'],
                'text/plain': ['.txt'],
                'image/png': ['.png'],
                'image/jpeg': ['.jpg', '.jpeg'],
              }}
              maxSize={50 * 1024 * 1024}
              maxFiles={5}
              multiple
              isProcessing={false}
              onSubmit={handleUpload}
              onCancel={() => setStep('method')}
              cancelLabel="Cancel"
              submitLabel="Generate Case Result"
              submitIcon={faSparkles}
              helperText="PDF, DOCX, TXT, PNG, or JPG — Maximum file size: 50 MB"
              errorMessage={uploadError}
              // TODO: Legal team is reviewing the privacy disclaimer copy. Once finalized, re-add the disclaimer footer here.
            />
          ))}
      </DialogContent>
    </Dialog>
  );
}
